import { Button, Col, Form, Row } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WorkerName from "./WorkerName";
import WorkerShortName from "./WorkerShortName";
import WorkerEmail from "./WorkerEmail";
import TotalFemaleCount from "./TotalFemaleCount";
import TotalThirdGender from "./TotalThirdGender";
import TotalMaleCount from "./TotalMaleCount";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  getChildOrganizationListRequest,
  updateChildOrganizationRequest,
  createChildOrganizationRequest,
  updateChildOrganization,
  createChildOrganization,
  createChildOrganizationClear,
  updateChildOrganizationClear,
  getLevelRequest,
  Organization,
  getOrganizationAdminRequest,
} from "@src/redux";
import AdminMobile from "../admin-details-form/AdminMobile";
import { retrieveItem } from "@src/helpers/functions";
import toast from "react-hot-toast";
import { useLocation, useParams } from "react-router-dom";
import LevelAttribute from "./LevelAttribute";

interface WorkersFormProps {
  handleCancel: () => void;
  mutationType: "edit" | "add";
  worker?: any;
}
interface mobileObject {
  number?: string;
  country_code?: string;
  country_short_code?: string;
}

const WorkersForm: React.FC<WorkersFormProps> = ({
  handleCancel,
  mutationType,
  worker,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { id } = useParams();
  const { id: organizationId } = useParams();
  const [mobile, setMobile] = useState<mobileObject>({});
  const { state } = useLocation();
  const {
    result: createOrganizationResult,
    loading: createOrganizationLoading,
  } = useSelector(createChildOrganization);
  const {
    result: updateOrganizationResult,
    loading: updateOrganizationLoading,
  } = useSelector(updateChildOrganization);

  const { myOrgResult }: { myOrgResult: Organization } =
    retrieveItem("my_org_result");
  useEffect(() => {
    if (updateOrganizationResult?.status === "success") {
      toast.success(t("update_organization_success"));
      dispatch(updateChildOrganizationClear());
      dispatch(
        getChildOrganizationListRequest({
          filter: {
            parentOrganizationId: organizationId || "",
          },
          pagination: {
            pageLimit: 10,
            pageNumber: 1,
          },
        })
      );
      handleCancel();
    } else if (updateOrganizationResult?.error?.message) {
      toast.error(updateOrganizationResult.error.message);
      dispatch(updateChildOrganizationClear());
      dispatch(
        getChildOrganizationListRequest({
          filter: {
            parentOrganizationId: organizationId || "",
          },
          pagination: {
            pageLimit: 10,
            pageNumber: 1,
          },
        })
      );
    } else if (createOrganizationResult?.status === "success") {
      toast.success(t("create_organization_success"));
      dispatch(createChildOrganizationClear());
      dispatch(
        getChildOrganizationListRequest({
          filter: {
            parentOrganizationId: organizationId || "",
          },
          pagination: {
            pageLimit: 10,
            pageNumber: 1,
          },
        })
      );
      handleCancel();
    } else if (createOrganizationResult?.error?.message) {
      toast.error(createOrganizationResult.error.message);
      dispatch(createChildOrganizationClear());
      dispatch(
        getChildOrganizationListRequest({
          filter: {
            parentOrganizationId: organizationId || "",
          },
          pagination: {
            pageLimit: 10,
            pageNumber: 1,
          },
        })
      );
    }
  }, [updateOrganizationResult, createOrganizationResult]);

  useEffect(() => {
    if (mutationType === "edit") {
      // dispatch(getOrganizationRequest({ filter: { id: worker?.id } }));
      form.setFieldsValue({
        ...worker,
        mobile: worker?.mobile?.number
          ? worker?.mobile?.country_short_code + worker?.mobile?.number
          : "",
      });
      setMobile(worker?.mobile);
    }
  }, [mutationType, worker, form]);
  useEffect(() => {
    if (state?.breadCrumbs?.length > 0) {
      let lastLevel = state?.breadCrumbs?.[state?.breadCrumbs.length - 1];
      dispatch(
        getLevelRequest({
          filter: { name: lastLevel?.name?.split("-")?.[1]?.trim() },
        })
      );
    }
  }, [state]);
  const handleChangeNumber = (values: {
    number: string;
    country_code: string;
    country_short_code: string;
  }) => {
    setMobile(values);
  };

  const onFinish = (values: any) => {
    if (mutationType === "add") {
      dispatch(
        createChildOrganizationRequest({
          data: {
            name: values?.name,
            shortName: values?.shortName,
            email: values?.email,
            mobile: mobile || worker?.mobile,
            parentOrganizationId: id || "",
            malePopulation: values?.malePopulation
              ? Number(values?.malePopulation)
              : undefined,
            femalePopulation: values?.femalePopulation
              ? Number(values?.femalePopulation)
              : undefined,
            thirdPopulation: values?.thirdPopulation
              ? Number(values?.thirdPopulation)
              : undefined,
            levelAttributeValueList:
              values?.levelAttributeValueList || undefined,
          },
        })
      );
    } else {
      dispatch(
        updateChildOrganizationRequest({
          id: worker?.id || "",
          data: {
            name: values?.name,
            shortName: values?.shortName,
            mobile: mobile || worker?.mobile,
            email: values?.email,
            malePopulation: values?.malePopulation
              ? Number(values?.malePopulation)
              : undefined,
            femalePopulation: values?.femalePopulation
              ? Number(values?.femalePopulation)
              : undefined,
            thirdPopulation: values?.thirdPopulation
              ? Number(values?.thirdPopulation)
              : undefined,
            levelAttributeValueList:
              values?.levelAttributeValueList || undefined,
          },
        })
      );
    }
  };

  return (
    <Form
      layout="vertical"
      className="workers-form"
      form={form}
      onFinish={onFinish}
    >
      <Row gutter={10}>
        <Col span={12}>
          <WorkerName />
        </Col>
        <Col span={12}>
          <WorkerShortName />
        </Col>
      </Row>
      <LevelAttribute form={form} />
      <Row gutter={10}>
        <Col span={12}>
          <AdminMobile onChange={handleChangeNumber} />
        </Col>
        <Col span={12}>
          <WorkerEmail />
        </Col>
      </Row>
      {worker?.level?.childLevelList &&
        worker?.level?.childLevelList?.length === 0 &&
        myOrgResult?.rootOrganization?.hasPopulationCount && (
          <Row gutter={10}>
            <Col span={8}>
              <TotalMaleCount />
            </Col>
            <Col span={8}>
              <TotalFemaleCount />
            </Col>
            <Col span={8}>
              <TotalThirdGender />
            </Col>
          </Row>
        )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button style={{ marginRight: "10px" }} onClick={handleCancel} danger>
          {t("close")}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={createOrganizationLoading || updateOrganizationLoading}
        >
          {mutationType === "edit" ? t("edit") : t("add")}
        </Button>
      </div>
    </Form>
  );
};

export default WorkersForm;
